import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { useMemo } from "react";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useWalmartOmsReauthorization(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const reauthenticateLink = useMemo(() => {
    if (!process.env.WALMART_APP_CLIENT_ID) {
      return;
    }

    const callbackUrl = new URL("https://login.account.wal-mart.com/authorize");
    const nonce = randomHexString(nativeRandom, 20);

    callbackUrl.searchParams.append("responseType", "code");
    callbackUrl.searchParams.append(
      "clientId",
      process.env.WALMART_APP_CLIENT_ID,
    );
    callbackUrl.searchParams.append(
      "redirectUri",
      "https://app-server.getredo.com/integrations/walmart/callback",
    );
    callbackUrl.searchParams.append("clientType", "seller");
    callbackUrl.searchParams.append("nonce", nonce);
    callbackUrl.searchParams.append("state", team._id);

    return callbackUrl.toString();
  }, [team._id]);

  const isTokenValid = useLoad(
    async (signal) => {
      if (!canAuthenticateWithThirdParties) {
        return undefined;
      }
      const result = await rpcClient.getWalmartIntegration({});
      return result.valid;
    },
    [rpcClient],
  );

  if (isTokenValid.value !== false) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Medium,
    item: {
      id: "walmart-oms-reauthorization",
      type: "warning",
      message:
        "Your Walmart OMS connection needs to be reauthorized to continue using Redo OMS.",
      actions: [
        {
          actionType: "link",
          href: reauthenticateLink!,
          text: "Reauthorize",
          internal: false,
        },
      ],
    },
  };
}
